import { useState } from 'react';
import { useTranslations } from 'use-intl';
import { addDays, todayISO } from '@shared/dates';
import type { Locale } from '@/i18n';
import { localePath } from '@/app/locale';
import { useReadiness, useTeamLoad, useTeams, type ReadinessRow } from '@/app/queries';
import { formatDate, sheetName } from '@/lib/format';
import { Spinner, Button } from '@/ui/Button';
import { EmptyState, PageHeader, Panel, Notice } from '@/ui/Panel';
import { LoadProfile } from '@/ui/LoadProfile';
import { Sheet, SheetNumber, SheetRow } from '@/ui/Sheet';
import { Explain } from '@/ui/Explain';
import { ProvenanceChip, ProvenanceNote } from '@/ui/Provenance';
import { weakest } from '@worker/domain/load/provenance';

/**
 * Readiness colour follows the intensity ramp, so a red row here means the same
 * kind of thing as a red bar on a load chart: more, not "bad".
 */
const STATUS_STYLE: Record<ReadinessRow['status'], string> = {
  fresh: 'bg-load-rest/12 text-load-rest',
  normal: 'bg-surface-sunken text-ink-muted',
  watch: 'bg-load-moderate/14 text-load-moderate',
  fatigued: 'bg-load-high/12 text-load-high',
  high_concern: 'bg-load-peak/12 text-load-peak',
};

export function Load({ locale }: { locale: Locale }) {
  const t = useTranslations();
  const teams = useTeams();
  const teamList = teams.data ?? [];
  const [teamId, setTeamId] = useState<string | null>(null);
  const activeTeamId = teamId ?? teamList[0]?.id ?? null;

  const today = todayISO();
  const [offset, setOffset] = useState(0);
  // Above the early returns below: a hook that runs on some renders and not
  // others is a hook-order error, and this one sat under two of them.
  const [showRest, setShowRest] = useState(false);
  const from = addDays(today, -27 + offset * 7);
  const to = addDays(today, 7 + offset * 7);

  const load = useTeamLoad(activeTeamId ?? undefined, from, to);
  const readiness = useReadiness(activeTeamId ?? undefined, today);

  if (teams.isLoading) {
    return (
      <p className="flex items-center gap-2 text-ink-muted">
        <Spinner /> {t('common.loading')}
      </p>
    );
  }

  if (teamList.length === 0) {
    return (
      <>
        <PageHeader
        title={t('load.title')}
        lead={t('load.lead')}
        help={localePath(locale, '/guide/numbers')}
      />
        <Panel title={t('load.title')}>
          <EmptyState title={t('teams.empty')} body={t('dashboard.noTeamBody')} />
        </Panel>
      </>
    );
  }

  const sessions = load.data?.sessions ?? [];
  const matches = load.data?.matches ?? [];
  const matchDates = new Set(matches.filter((m) => m.status !== 'cancelled').map((m) => m.onDate));

  // One bar per day across the window, so gaps between sessions are visible as
  // rest rather than closed up into a misleadingly continuous line.
  const days: Array<{
    date: string;
    mdLabel: string | null;
    plannedLoad: number | null;
    actualLoad: number | null;
    isMatch: boolean;
    isToday: boolean;
  }> = [];
  for (let d = from; d <= to; d = addDays(d, 1)) {
    const onDay = sessions.filter((s) => s.onDate === d);
    days.push({
      date: d,
      mdLabel: null,
      plannedLoad: onDay.reduce((s, x) => s + (x.plannedLoad ?? 0), 0) || null,
      actualLoad: onDay.reduce((s, x) => s + (x.actualLoad ?? 0), 0) || null,
      isMatch: matchDates.has(d),
      isToday: d === today,
    });
  }

  const hasLoad = sessions.some((s) => s.actualLoad || s.plannedLoad);
  const players = readiness.data ?? [];

  // What the squad's figures rest on, so a row only has to speak up when its
  // own is different. The weakest, matching what the engine reports per player.
  const squadProvenance = weakest(players.map((p) => p.provenance));

  /*
    Who the engine actually named, and everyone else.

    Readiness only speaks about players whose signals it can name (D-017), and
    listing all twenty-four at equal weight made this one panel four and a half
    screens — with the two the coach should look at buried among twenty-two it
    has nothing to say about. The rest are one tap away and the button counts
    them, so nobody disappears.
  */
  const worthLooking = players.filter(
    (p) => !p.insufficientHistory && p.status !== 'normal' && p.status !== 'fresh',
  );
  const rest = players.filter((p) => !worthLooking.includes(p));
  // When nothing is worth looking at, readiness says so and stays quiet —
  // D-017's stance, and the difference between a screen that reports and a
  // screen that answers. The squad is still one tap away.
  const shown = showRest ? players : worthLooking;
  const raised = players.filter((p) => p.signals.length > 0 && !p.insufficientHistory);

  return (
    <>
      <PageHeader
        title={t('load.title')}
        lead={t('load.lead')}
        help={localePath(locale, '/guide/numbers')}
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {teamList.length > 1
          ? teamList.map((tm) => (
              <button
                key={tm.id}
                type="button"
                onClick={() => setTeamId(tm.id)}
                aria-pressed={tm.id === activeTeamId}
                className={`rounded-(--radius-control) border px-3 py-1.5 text-meta transition-colors ${
                  tm.id === activeTeamId
                    ? 'border-ink bg-ink text-paper'
                    : 'border-line bg-surface text-ink-muted hover:border-ink'
                }`}
              >
                {tm.name}
              </button>
            ))
          : null}
        <div className="ms-auto flex gap-1">
          <Button
            variant="secondary"
            className="!min-w-11 !px-3 !text-meta"
            onClick={() => setOffset((o) => o - 1)}
          >
            ‹
          </Button>
          <Button
            variant="secondary"
            className="!text-meta"
            onClick={() => setOffset(0)}
          >
            {t('calendar.today')}
          </Button>
          <Button
            variant="secondary"
            className="!min-w-11 !px-3 !text-meta"
            onClick={() => setOffset((o) => o + 1)}
          >
            ›
          </Button>
        </div>
      </div>

      <Panel
        title={t('load.weeklyLoad')}
        explain="session_load"
        count={`${formatDate(from, locale)} – ${formatDate(to, locale)}`}
      >
        {load.isLoading ? (
          <p className="flex items-center gap-2 px-4 py-8 text-ink-muted">
            <Spinner /> {t('common.loading')}
          </p>
        ) : !hasLoad ? (
          <EmptyState title={t('load.empty')} body={t('load.emptyBody')} />
        ) : (
          <LoadProfile days={days} />
        )}
      </Panel>

      <div className="mt-5">
        <Panel title={t('load.readiness')} explain="readiness" count={players.length || undefined}>
          {/* The ratio is printed once per player, so the term is anchored to
              the line that says what the numbers are rather than twenty-four
              times down the list. */}
          <p className="flex flex-wrap items-center gap-x-3 border-b border-line px-4 py-3 text-meta text-ink-muted">
            {t('load.readinessHint')}
            <Explain term="acute_chronic" />
          </p>

          {/* Said once for the squad as well as per row: twenty-four rows each
              repeating "Derived from the session rating" is noise, and the set
              is the thing a coach reads. */}
          {players.length > 0 ? (
            <p className="flex flex-wrap items-center gap-x-3 border-b border-line px-4 py-2 text-meta text-ink-faint">
              <ProvenanceNote values={players.map((p) => p.provenance)} />
              <Explain term="provenance" />
            </p>
          ) : null}

          {readiness.isLoading ? (
            <p className="flex items-center gap-2 px-4 py-8 text-ink-muted">
              <Spinner /> {t('common.loading')}
            </p>
          ) : players.length === 0 ? (
            <EmptyState title={t('squad.empty')} body={t('squad.emptyBody')} />
          ) : players.every((p) => p.insufficientHistory) ? (
            <EmptyState title={t('load.noHistory')} body={t('load.noHistoryBody')} />
          ) : (
            <>
              {raised.length > 0 ? (
                <div className="px-4 pt-4">
                  <Notice tone={raised.some((p) => p.signals.length > 1) ? 'risk' : 'info'}>
                    {raised.some((p) => p.signals.length > 1)
                      ? t('load.multipleIndicators')
                      : t('load.oneIndicator')}{' '}
                    {t('load.decideYourself')}
                  </Notice>
                </div>
              ) : null}

              {shown.length === 0 ? (
                <p className="px-4 py-6 text-meta text-ink-muted">{t('load.nobodySignalling')}</p>
              ) : null}

              <Sheet>
                {shown.map((p) => (
                  <SheetRow key={p.playerId} wrap>
                    <SheetNumber>{p.shirtNumber ?? '–'}</SheetNumber>
                    <span className="min-w-0 flex-1">
                      {/* Wraps rather than truncates. D-042 settled this for
                          the printed call-up — "Mohammed AMINE E…" is not a
                          name a player can find himself in — and a coach
                          scanning a readiness list has the same problem. */}
                      <span className="block font-medium">
                        {sheetName(p.firstName, p.lastName)}
                      </span>
                      {p.insufficientHistory ? (
                        <span className="text-meta text-ink-faint">{t('load.noHistory')}</span>
                      ) : p.signals.length > 0 ? (
                        <span className="block text-meta text-ink-muted">
                          {p.signals
                            .map((s) => t(`load.signal.${s.split('.').pop()}`))
                            .join(' · ')}
                        </span>
                      ) : null}
                      {/* Only when this player's figure rests on something
                          different from the squad's. Twenty-four rows all
                          saying "Derived" is noise the note above already
                          covers; the one row that says "Measured" is the thing
                          worth seeing. */}
                      {!p.insufficientHistory && p.provenance !== squadProvenance ? (
                        <span className="mt-1 block">
                          <ProvenanceChip value={p.provenance} />
                        </span>
                      ) : null}
                    </span>
                    {!p.insufficientHistory ? (
                      <>
                        <span className="tabular hidden w-20 shrink-0 text-end text-meta text-ink-faint sm:inline-block">
                          {Math.round(p.acute7)} / {Math.round(p.chronicWeekly)}
                        </span>
                        <span className="tabular w-12 shrink-0 text-end font-condensed text-meta font-semibold">
                          {p.ratio.toFixed(2)}
                        </span>
                      </>
                    ) : null}
                    <span
                      className={`w-24 shrink-0 rounded-[3px] px-2 py-0.5 text-center text-meta ${
                        STATUS_STYLE[p.status]
                      }`}
                    >
                      {t(`load.status_${p.status}`)}
                    </span>
                  </SheetRow>
                ))}
              </Sheet>

              {!showRest && rest.length > 0 ? (
                <div className="border-t border-line p-4">
                  <Button variant="secondary" onClick={() => setShowRest(true)}>
                    {t('load.showRest', { count: rest.length })}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </Panel>
      </div>
    </>
  );
}
